/**
 * The Olloport panel — one place for everything the fork used to spread over
 * the view panel, the lighting panel and three floating tool panels.
 *
 * Opens from the right toolbar like Appearance / Overlays / Preferences and
 * follows the same options-panel conventions (section bars, label + control
 * rows, help tooltips). Sections:
 *
 *   Scene       sign-in state, save to the backend, export a bundle, preview
 *   Media       starting view, cover thumbnail, hover-preview video
 *   Navigation  how the viewer moves (free / rotating) and the orbit limits
 *   Tools       markers, walk area, align — each opens its own tool panel
 *
 * Labels never clip: every row label carries its full text as a tooltip and
 * hints wrap. State comes from events only, so a document load, a sidecar
 * apply or a tool all refresh the panel the same way.
 */

import { BooleanInput, Button, Container, Label, NumericInput, SelectInput } from '@playcanvas/pcui';

import type { Events } from '../../events';
import { i18n } from '../../ui/localization';
import type { Tooltips } from '../../ui/tooltips';
import { getSession, logout } from '../backend';
import type { StartPose } from '../start-view';
import type { LightingSettings } from '../lighting';
import type { ViewerSettings } from '../viewer-settings';

class OlloportPanel extends Container {
    constructor(events: Events, tooltips: Tooltips, args = {}) {
        args = {
            ...args,
            id: 'ollo-panel',
            class: ['panel', 'options-panel', 'ollo-panel'],
            hidden: true
        };
        super(args);

        ['pointerdown', 'pointerup', 'pointermove', 'wheel', 'dblclick'].forEach((eventName) => {
            this.dom.addEventListener(eventName, (event: Event) => event.stopPropagation());
        });

        // ── header ──────────────────────────────────────────────────────
        const header = new Container({ class: 'panel-header' });
        const icon = new Label({ text: '', class: 'panel-header-icon' });
        const title = new Label({ class: 'panel-header-label' });
        i18n.bindText(title, 'ollo.panel');
        header.append(icon);
        header.append(title);
        this.append(header);

        // ── building blocks ─────────────────────────────────────────────
        const section = (key: string) => {
            const bar = new Container({ class: ['panel-header', 'options-panel-section'] });
            const label = new Label({ class: 'panel-header-label' });
            i18n.bindText(label, key);
            bar.append(label);
            this.append(bar);
        };

        const row = (labelKey: string, control: Container | Label | Button | BooleanInput | SelectInput | NumericInput, tooltipKey?: string, extraClass?: string) => {
            const r = new Container({ class: ['settings-panel-row', 'ollo-panel-row'].concat(extraClass ? [extraClass] : []) });
            const label = new Label({ class: ['settings-panel-row-label', 'ollo-panel-row-label'] });
            i18n.bindText(label, labelKey);
            // the full label text, for when the column is narrower than the words
            tooltips.register(label, () => i18n.t(labelKey), 'top');
            r.append(label);
            r.append(control);
            if (tooltipKey) tooltips.register(r, () => i18n.t(tooltipKey), 'left');
            this.append(r);
            return r;
        };

        const buttons = (...items: Button[]) => {
            const r = new Container({ class: 'ollo-panel-buttons' });
            items.forEach(b => r.append(b));
            this.append(r);
            return r;
        };

        const button = (key: string, cls: string | string[] = 'ollo-panel-button') => {
            const b = new Button({ class: cls });
            i18n.bindText(b, key);
            return b;
        };

        const hint = (key: string) => {
            const l = new Label({ class: 'ollo-panel-hint' });
            i18n.bindText(l, key);
            this.append(l);
            return l;
        };

        const status = (cls = 'ollo-panel-status') => new Label({ class: cls, text: '' });

        // ── scene: account, save, export, preview ───────────────────────
        section('ollo.section.scene');

        const sessionLabel = status();
        const sessionRow = new Container({ class: 'ollo-panel-status-row' });
        sessionRow.append(sessionLabel);
        this.append(sessionRow);

        const signIn = button('ollo.scene.sign-in');
        const signOut = button('ollo.scene.sign-out');
        const save = button('ollo.scene.save', ['ollo-panel-button', 'ollo-panel-button-primary']);
        const exportBundle = button('ollo.scene.export');
        const preview = button('ollo.scene.preview');
        buttons(signIn, signOut);
        buttons(save, exportBundle, preview);

        let signedIn = false;
        const refreshSession = async () => {
            const user = await getSession();
            signedIn = !!user;
            sessionLabel.text = user ?
                `${i18n.t('ollo.scene.signed-in')} ${user.email}` :
                i18n.t('ollo.scene.signed-out');
            signIn.hidden = signedIn;
            signOut.hidden = !signedIn;
        };
        signIn.on('click', async () => {
            await events.invoke('show.loginDialog');
            await refreshSession();
        });
        signOut.on('click', async () => {
            await logout();
            await refreshSession();
        });
        save.on('click', () => events.invoke('ollo.save'));
        exportBundle.on('click', () => events.invoke('scene.exportBundle'));
        preview.on('click', () => events.fire('viewerPreview.toggle'));
        events.on('viewerPreview.visible', (visible: boolean) => {
            preview.class[visible ? 'add' : 'remove']('active');
        });
        const updateSceneButtons = () => {
            const empty = !!events.invoke('scene.empty');
            save.enabled = !empty;
            exportBundle.enabled = !empty;
            preview.enabled = !empty;
        };
        events.on('scene.elementAdded', updateSceneButtons);
        events.on('scene.elementRemoved', updateSceneButtons);
        events.on('scene.clear', updateSceneButtons);

        // ── media: start view, thumbnail, preview video ─────────────────
        section('ollo.section.media');

        const startStatus = status();
        row('ollo.media.start-view', startStatus, 'ollo.media.start-view.tooltip');
        const startSet = button('ollo.media.set');
        const startGo = button('ollo.media.go');
        const startClear = button('ollo.media.clear');
        buttons(startSet, startGo, startClear);
        startSet.on('click', () => events.fire('camera.setStartPose'));
        startGo.on('click', () => events.fire('camera.gotoStartPose'));
        startClear.on('click', () => events.fire('camera.clearStartPose'));
        const refreshStart = (pose: StartPose | null) => {
            startStatus.text = pose ? i18n.t('ollo.media.start-view.set') : i18n.t('ollo.media.start-view.live');
            startGo.enabled = !!pose;
            startClear.enabled = !!pose;
        };
        events.on('camera.startPose', refreshStart);
        refreshStart((events.invoke('camera.getStartPose') as StartPose | null) ?? null);

        const thumbStatus = status();
        row('ollo.media.thumbnail', thumbStatus, 'ollo.media.thumbnail.tooltip');
        const thumbImg = document.createElement('img');
        thumbImg.className = 'ollo-panel-thumbnail';
        thumbImg.hidden = true;
        this.dom.appendChild(thumbImg);
        const thumbCapture = button('ollo.media.capture');
        const thumbClear = button('ollo.media.clear');
        buttons(thumbCapture, thumbClear);
        thumbCapture.on('click', () => events.invoke('thumbnail.capture'));
        thumbClear.on('click', () => events.fire('thumbnail.clear'));
        const refreshThumb = (dataUrl: string | null) => {
            thumbStatus.text = dataUrl ? i18n.t('ollo.media.thumbnail.captured') : i18n.t('ollo.media.thumbnail.none');
            thumbImg.hidden = !dataUrl;
            if (dataUrl) thumbImg.src = dataUrl;
            thumbClear.enabled = !!dataUrl;
        };
        events.on('thumbnail.changed', refreshThumb);
        refreshThumb((events.invoke('thumbnail.get') as string | null) ?? null);

        const videoStatus = status();
        row('ollo.media.preview-video', videoStatus, 'ollo.media.preview-video.tooltip');
        const videoRender = button('ollo.media.render');
        const videoClear = button('ollo.media.clear');
        buttons(videoRender, videoClear);
        videoRender.on('click', () => events.invoke('render.previewVideo'));
        videoClear.on('click', () => events.fire('preview.clear'));
        const refreshVideo = (has: boolean) => {
            const bytes = has ? (events.invoke('preview.getBytes') as Uint8Array | null)?.byteLength ?? 0 : 0;
            videoStatus.text = has ?
                `${i18n.t('ollo.media.preview-video.ready')} (${(bytes / 1048576).toFixed(1)} MB)` :
                i18n.t('ollo.media.preview-video.none');
            videoClear.enabled = has;
        };
        events.on('preview.changed', refreshVideo);
        refreshVideo(!!events.invoke('preview.get'));

        // ── navigation: viewer behaviour ─────────────────────────────────
        section('ollo.section.navigation');

        const mode = new SelectInput({
            class: 'settings-panel-row-select',
            defaultValue: 'free',
            options: [
                { v: 'free', t: i18n.t('ollo.nav.mode.free') },
                { v: 'rotating', t: i18n.t('ollo.nav.mode.rotating') }
            ]
        });
        i18n.onChange(() => {
            mode.options = [
                { v: 'free', t: i18n.t('ollo.nav.mode.free') },
                { v: 'rotating', t: i18n.t('ollo.nav.mode.rotating') }
            ];
        }, mode);
        row('ollo.nav.mode', mode, 'ollo.nav.mode.tooltip');

        const numeric = (min: number, max: number, precision: number, step: number) => new NumericInput({
            class: 'settings-panel-row-numeric', min, max, precision, step, hideSlider: true
        });
        const pitchMin = numeric(0, 89, 0, 1);
        const zoomMin = numeric(0, 1000, 2, 0.1);
        const zoomMax = numeric(0, 1000, 2, 0.1);
        const autoRotate = new BooleanInput({ type: 'toggle', class: 'settings-panel-row-toggle' });
        const autoSpeed = numeric(0, 60, 1, 0.5);
        const autoDelay = numeric(0, 120, 1, 0.5);
        const showBounds = new BooleanInput({ type: 'toggle', class: 'settings-panel-row-toggle' });

        const orbitRows = [
            row('ollo.nav.pitch-min', pitchMin, 'ollo.nav.pitch-min.tooltip', 'options-panel-row-indent'),
            row('ollo.nav.zoom-min', zoomMin, 'ollo.nav.zoom.tooltip', 'options-panel-row-indent'),
            row('ollo.nav.zoom-max', zoomMax, 'ollo.nav.zoom.tooltip', 'options-panel-row-indent'),
            row('ollo.nav.auto-rotate', autoRotate, 'ollo.nav.auto-rotate.tooltip', 'options-panel-row-indent'),
            row('ollo.nav.auto-rotate-speed', autoSpeed, 'ollo.nav.auto-rotate.tooltip', 'options-panel-row-indent'),
            row('ollo.nav.auto-rotate-delay', autoDelay, 'ollo.nav.auto-rotate.tooltip', 'options-panel-row-indent'),
            row('ollo.nav.show-bounds', showBounds, 'ollo.nav.show-bounds.tooltip', 'options-panel-row-indent')
        ];

        let syncing = false;
        const readSettings = () => {
            const s = events.invoke('viewer.settings') as ViewerSettings | undefined;
            if (!s) return;
            syncing = true;
            try {
                mode.value = s.navigation;
                pitchMin.value = s.orbit.pitchMinDeg ?? 16;
                zoomMin.value = s.orbit.zoomMinM ?? 0;
                zoomMax.value = s.orbit.zoomMaxM ?? 0;
                autoRotate.value = s.orbit.autoRotate ?? true;
                autoSpeed.value = s.orbit.autoRotateSpeed ?? 3;
                autoDelay.value = s.orbit.autoRotateDelay ?? 5;
                const rotating = s.navigation === 'rotating';
                orbitRows.forEach(r => { r.hidden = !rotating; });
            } finally {
                syncing = false;
            }
        };
        const writeSettings = () => {
            if (syncing) return;
            events.fire('viewer.setSettings', {
                navigation: mode.value as ViewerSettings['navigation'],
                orbit: {
                    pitchMinDeg: pitchMin.value,
                    zoomMinM: zoomMin.value,
                    zoomMaxM: zoomMax.value,
                    autoRotate: autoRotate.value,
                    autoRotateSpeed: autoSpeed.value,
                    autoRotateDelay: autoDelay.value
                }
            });
        };
        [mode, pitchMin, zoomMin, zoomMax, autoRotate, autoSpeed, autoDelay].forEach(c => c.on('change', writeSettings));
        showBounds.on('change', (v: boolean) => events.fire('orbitBounds.setVisible', v));
        events.on('viewer.settingsChanged', readSettings);
        readSettings();
        showBounds.value = !!events.invoke('orbitBounds.visible');

        // ── lighting: what the viewer applies (scene.json lighting) ──────
        section('ollo.section.lighting');
        const tonemap = new SelectInput({
            class: 'settings-panel-row-select',
            defaultValue: 'linear',
            options: [
                { v: 'linear', t: 'Linear' }, { v: 'filmic', t: 'Filmic' }, { v: 'hejl', t: 'Hejl' },
                { v: 'aces', t: 'ACES' }, { v: 'aces2', t: 'ACES 2' }, { v: 'neutral', t: 'Neutral' }
            ]
        });
        const exposure = numeric(-4, 4, 2, 0.1);
        const contrast = numeric(0, 2, 2, 0.05);
        const saturation = numeric(0, 2, 2, 0.05);
        const bloom = new BooleanInput({ type: 'toggle', class: 'settings-panel-row-toggle' });
        const bloomIntensity = numeric(0, 0.5, 3, 0.005);
        row('ollo.light.tonemap', tonemap, 'ollo.light.tonemap.tooltip');
        row('ollo.light.exposure', exposure, 'ollo.light.exposure.tooltip');
        row('ollo.light.contrast', contrast, 'ollo.light.contrast.tooltip');
        row('ollo.light.saturation', saturation, 'ollo.light.saturation.tooltip');
        row('ollo.light.bloom', bloom, 'ollo.light.bloom.tooltip');
        const bloomRow = row('ollo.light.bloom-intensity', bloomIntensity, 'ollo.light.bloom.tooltip', 'options-panel-row-indent');
        const resetLighting = button('ollo.light.reset');
        buttons(resetLighting);
        let lightSyncing = false;
        const readLighting = () => {
            const l = events.invoke('lighting.get') as LightingSettings | undefined;
            if (!l) return;
            lightSyncing = true;
            try {
                tonemap.value = l.tonemap;
                exposure.value = l.exposure;
                contrast.value = l.contrast;
                saturation.value = l.saturation;
                bloom.value = l.bloom.enabled;
                bloomIntensity.value = l.bloom.intensity;
                bloomRow.hidden = !l.bloom.enabled;
            } finally {
                lightSyncing = false;
            }
        };
        const writeLighting = () => {
            if (lightSyncing) return;
            events.fire('lighting.set', {
                tonemap: tonemap.value,
                exposure: exposure.value,
                contrast: contrast.value,
                saturation: saturation.value,
                bloom: { enabled: bloom.value, intensity: bloomIntensity.value }
            });
        };
        [tonemap, exposure, contrast, saturation, bloom, bloomIntensity].forEach(c => c.on('change', writeLighting));
        resetLighting.on('click', () => events.fire('lighting.reset'));
        events.on('lighting.changed', readLighting);
        readLighting();

        // ── authoring tools ─────────────────────────────────────────────
        section('ollo.section.tools');
        hint('ollo.tools.hint');
        const markers = button('ollo.tools.markers');
        const walk = button('ollo.tools.walk');
        const align = button('ollo.tools.align');
        buttons(markers, walk, align);
        const openTool = (panel: 'markers' | 'walk' | 'align') => {
            (['markers', 'walk', 'align'] as const).forEach((p) => {
                events.fire(`${p}.panel.${p === panel ? 'show' : 'hide'}`);
            });
            // the tool panel takes this panel's slot next to the toolbar
            setVisible(false);
        };
        markers.on('click', () => openTool('markers'));
        walk.on('click', () => openTool('walk'));
        align.on('click', () => openTool('align'));

        // ── visibility, exclusive with the other option panels ──────────
        const setVisible = (visible: boolean) => {
            if (visible === this.hidden) {
                this.hidden = !visible;
                events.fire('olloPanel.visible', visible);
                if (visible) {
                    void refreshSession();
                    updateSceneButtons();
                }
            }
        };
        events.function('olloPanel.visible', () => !this.hidden);
        events.on('olloPanel.setVisible', setVisible);
        events.on('olloPanel.toggleVisible', () => setVisible(this.hidden));
        ['settingsPanel.visible', 'overlaysPanel.visible', 'appearancePanel.visible'].forEach((name) => {
            events.on(name, (visible: boolean) => {
                if (visible) setVisible(false);
            });
        });

        updateSceneButtons();
        void refreshSession();
    }
}

export { OlloportPanel };
